// Code generated by contracts/generate.ts. DO NOT EDIT.
//
// Edit the JSON under contracts/data/ and run `pnpm contracts:generate`.
// CI fails if this file is hand-edited or left stale.

/** Stamped on every rich event document. A document without it is a legacy bare event. */
export const ANALYTICS_EVENT_SCHEMA_VERSION = 2;

/** A song by id only, so a rename never leaves stale text in old events. */
export interface AnalyticsSongRef {
  songId: string;
  songVersion?: string;
}

/**
 * One settled volume-slider interaction: the value it started at and the
 * value it ended at.
 */
export interface TrackVolumeChangedPayload extends AnalyticsSongRef {
  track: string;
  from: number;
  to: number;
}

export interface TrackRangeChangedPayload extends AnalyticsSongRef {
  track: string;
  from: string;
  to: string;
}

export interface TrackMuteChangedPayload extends AnalyticsSongRef {
  track: string;
  muted: boolean;
}

export interface TrackSoloChangedPayload extends AnalyticsSongRef {
  track: string;
  solo: boolean;
}

/**
 * Written once per stretch of playback, when it stops, the song changes, the
 * song ends or the app is backgrounded.
 */
export interface SongPlaySessionPayload extends AnalyticsSongRef {
  seconds: number;
  endedBy: 'stop' | 'songChange' | 'end' | 'background';
}

/** Added to My Songs. */
export type SongAddedPayload = AnalyticsSongRef;

/** Removed from My Songs. */
export type SongRemovedPayload = AnalyticsSongRef;

export interface PlaylistCreatedPayload {
  playlistId: string;
}

export interface PlaylistSongsAddedPayload {
  playlistId: string;
  songIds: string[];
}

export interface PlaylistSongsRemovedPayload {
  playlistId: string;
  songIds: string[];
}

export interface RepeatModeChangedPayload {
  mode: 'off' | 'one' | 'all';
  songId?: string;
  playlistId?: string;
}

export type SongRepeatedPayload = AnalyticsSongRef;

export interface PlaylistRepeatedPayload {
  playlistId: string;
}

export interface AnalyticsEventPayloads {
  trackVolumeChanged: TrackVolumeChangedPayload;
  trackRangeChanged: TrackRangeChangedPayload;
  trackMuteChanged: TrackMuteChangedPayload;
  trackSoloChanged: TrackSoloChangedPayload;
  songPlaySession: SongPlaySessionPayload;
  songAdded: SongAddedPayload;
  songRemoved: SongRemovedPayload;
  playlistCreated: PlaylistCreatedPayload;
  playlistSongsAdded: PlaylistSongsAddedPayload;
  playlistSongsRemoved: PlaylistSongsRemovedPayload;
  repeatModeChanged: RepeatModeChangedPayload;
  songRepeated: SongRepeatedPayload;
  playlistRepeated: PlaylistRepeatedPayload;
}

export type AnalyticsEventName = keyof AnalyticsEventPayloads;

export const ANALYTICS_EVENT_NAMES: readonly AnalyticsEventName[] = Object.freeze([
  'trackVolumeChanged',
  'trackRangeChanged',
  'trackMuteChanged',
  'trackSoloChanged',
  'songPlaySession',
  'songAdded',
  'songRemoved',
  'playlistCreated',
  'playlistSongsAdded',
  'playlistSongsRemoved',
  'repeatModeChanged',
  'songRepeated',
  'playlistRepeated',
]);

/** Events whose payload starts with an AnalyticsSongRef. */
export const SONG_SCOPED_ANALYTICS_EVENTS: ReadonlySet<AnalyticsEventName> = new Set<AnalyticsEventName>([
  'trackVolumeChanged',
  'trackRangeChanged',
  'trackMuteChanged',
  'trackSoloChanged',
  'songPlaySession',
  'songAdded',
  'songRemoved',
  'songRepeated',
]);

export interface AnalyticsEventEnvelope<K extends AnalyticsEventName = AnalyticsEventName> {
  eventName: K;
  schemaVersion: typeof ANALYTICS_EVENT_SCHEMA_VERSION;
  appVersion?: string;
  /** A server timestamp: written as a sentinel, read back as a Firestore Timestamp. */
  timestamp?: unknown;
}

/** A rich event document exactly as it sits in Firestore: envelope and payload side by side. */
export type AnalyticsEventDocument = {
  [K in AnalyticsEventName]: AnalyticsEventEnvelope<K> & AnalyticsEventPayloads[K];
}[AnalyticsEventName];
